import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Clock, MapPin, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SHOLAT_NAMES = {
  Fajr: 'Subuh', Sunrise: 'Syuruq', Dhuhr: 'Dzuhur',
  Asr: 'Ashar', Maghrib: 'Maghrib', Isha: 'Isya'
};
const SHOW_KEYS = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
const PRAYER_KEYS = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];

function parseTime(raw, offsetDays = 0) {
  const clean = (raw || '00:00').replace(/ \(\+07\)| \(WIB\)/g, '');
  const [h, m] = clean.split(':').map(Number);
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(h, m, 0, 0);
  return d;
}

function getNextPrayer(timings) {
  if (!timings) return null;
  const now = new Date();
  for (const key of PRAYER_KEYS) {
    const t = parseTime(timings[key]);
    if (t > now) return { key, name: SHOLAT_NAMES[key], time: t };
  }
  // All passed → tomorrow's Fajr
  const t = parseTime(timings['Fajr'], 1);
  return { key: 'Fajr', name: 'Subuh', time: t };
}

function fmtCountdown(ms) {
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
}

function isNextPrayer(key, timings) {
  const now = new Date();
  const t = parseTime(timings[key]);
  const diff = t - now;
  return diff > 0 && diff < 3 * 60 * 60 * 1000;
}

function isPastPrayer(key, timings) {
  return parseTime(timings[key]) < new Date();
}

export default function JadwalSholatPage() {
  const [timings, setTimings] = useState(null);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    fetchSholat();
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const fetchSholat = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sholat/today`);
      setTimings(data.data?.timings);
      setDate(data.data?.date);
    } catch {} finally { setLoading(false); }
  };

  const fmt = (t) => t ? t.replace(' (WIB)', '').replace(' (+07)', '') : '--:--';

  const days = ['Minggu','Senin','Selasa','Rabu','Kamis','Jumat','Sabtu'];
  const months = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
  const today = new Date();
  const dateStr = `${days[today.getDay()]}, ${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`;
  const timeStr = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });

  const hijri = date?.hijri ? `${date.hijri.day} ${date.hijri.month.ar} ${date.hijri.year}H` : '';

  const nextPrayer = timings ? getNextPrayer(timings) : null;
  const countdown = nextPrayer ? fmtCountdown(nextPrayer.time - now) : null;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 px-6 pt-12 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
              <MapPin size={12} className="text-emerald-300" />
              <span className="text-xs text-white font-medium">Dabo Singkep, Lingga</span>
            </div>
            <button onClick={fetchSholat} data-testid="refresh-sholat-btn"
              className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors">
              <RefreshCw size={16} />
            </button>
          </div>
          <div className="text-center">
            <p className="text-5xl font-bold text-white tracking-tight font-mono" data-testid="current-time">{timeStr}</p>
            <p className="text-emerald-200 text-sm mt-1">{dateStr}</p>
            {hijri && <p className="font-amiri text-amber-300 text-base mt-1" dir="rtl">{hijri}</p>}
          </div>
        </div>

        {/* ─── COUNTDOWN ─────────────────────── */}
        {countdown && nextPrayer && (
          <div className="mx-4 -mt-3 mb-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-md border border-emerald-100 px-5 py-4 flex items-center justify-between"
              data-testid="countdown-box"
            >
              <div>
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest">Menuju Waktu</p>
                <p className="text-base font-bold text-emerald-800 mt-0.5">{nextPrayer.name}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-2xl font-bold text-emerald-600" data-testid="countdown-timer">{countdown}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{fmt(timings?.[nextPrayer.key])}</p>
              </div>
            </motion.div>
          </div>
        )}

        <div className="px-4 py-3 pb-28">
          <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Clock size={13} className="text-emerald-500" /> Jadwal Sholat Hari Ini
          </h2>

          {loading ? (
            <div className="text-center py-10 text-gray-400 text-sm">Memuat jadwal sholat...</div>
          ) : (
            <div className="space-y-2">
              {SHOW_KEYS.map((key, i) => {
                const isNext = timings && isNextPrayer(key, timings);
                const isPast = timings && isPastPrayer(key, timings);
                return (
                  <motion.div key={key}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    data-testid={`sholat-${key.toLowerCase()}`}
                    className={`flex items-center justify-between p-4 rounded-2xl transition-all ${
                      isNext ? 'bg-emerald-500 shadow-lg text-white' :
                      isPast ? 'bg-white opacity-50 shadow-sm' : 'bg-white shadow-sm'
                    }`}>
                    <div className="flex items-center gap-3">
                      {isNext && <div className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />}
                      <p className={`font-semibold text-sm ${isNext ? 'text-white' : 'text-gray-800'}`}>
                        {SHOLAT_NAMES[key]}
                      </p>
                      {isNext && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full text-white">Segera</span>}
                    </div>
                    <p className={`font-bold text-base ${isNext ? 'text-white' : 'text-emerald-700'}`}>
                      {timings ? fmt(timings[key]) : '--:--'}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="mt-4 bg-emerald-50 rounded-2xl p-4 text-center">
            <p className="text-xs text-emerald-600 font-medium">Koordinat Terkunci untuk</p>
            <p className="text-sm font-semibold text-emerald-800 mt-0.5">Dabo Singkep, Kabupaten Lingga</p>
            <p className="text-xs text-emerald-500 mt-0.5">Lat: -0.4769° | Lon: 104.5774°</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
